import React, { useState, useEffect, useCallback } from 'react';
import { FaUsers, FaFileExport, FaSync, FaSearch, FaFilter } from 'react-icons/fa';
import { Row, Col, Button, Form, Pagination, Spinner, Table, Modal, Alert, Card, Tabs, Tab } from 'react-bootstrap';
import axios from 'axios';
import './bradfactor.css';
import moment from 'moment';

export default function BradFactor() {
  const currentDate = new Date();
  const currentMonth = currentDate.getMonth() + 1;
  const currentYear = currentDate.getFullYear();

  const [rawData, setRawData] = useState([]);
  const [filteredData, setFilteredData] = useState([]);
  const [displayData, setDisplayData] = useState([]);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState('');
  const [filters, setFilters] = useState({
    month: '',
    year: currentYear.toString(),
    page: 1,
    limit: 10,
    viewMode: 'yearly',
    riskLevel: null,
  });
  const [pagination, setPagination] = useState({
    totalDocs: 0,
    totalPages: 1,
    hasNextPage: false,
    hasPrevPage: false,
  });
  const [showGenerateModal, setShowGenerateModal] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [generateMonth, setGenerateMonth] = useState('');
  const [generateYear, setGenerateYear] = useState('');
  const [error, setError] = useState(null);
  const [success, setSuccess] = useState(null);
  const [dataAvailable, setDataAvailable] = useState(true);
  const [overallRiskCounts, setOverallRiskCounts] = useState({
    high: 0,
    medium: 0,
    low: 0,
  });

  // Standard Bradford Factor risk thresholds
  const RISK_THRESHOLDS = {
    high: 100,
    medium: 50,
    low: 0
  };

  const calculateRiskCounts = (data) => {
    let high = 0, medium = 0, low = 0;

    if (!Array.isArray(data)) {
      console.warn('calculateRiskCounts received non-array data:', data);
      return { high, medium, low };
    }

    data.forEach(item => {
      const bf = Number(item.bradfordFactor) || 0;
      if (bf > RISK_THRESHOLDS.high) {
        high++;
      } else if (bf > RISK_THRESHOLDS.medium && bf <= RISK_THRESHOLDS.high) {
        medium++;
      } else {
        low++;
      }
    });

    return { high, medium, low };
  };

  const aggregateDataByEmployee = useCallback((data) => {
    if (!Array.isArray(data)) {
      console.warn('aggregateDataByEmployee received non-array data:', data);
      return [];
    }

    const aggregated = {};
    data.forEach((item) => {
      const userName = item.userName || 'Unknown';
      const department = item.department || 'Not specified';
      const key = `${userName}_${department}_${filters.year || item.year || 'N/A'}`;

      if (!aggregated[key]) {
        aggregated[key] = {
          userName,
          department,
          _id: key,
          absentDays: 0,
          workingDays: 0,
          occasionsOfAbsence: 0,
          bradfordFactor: 0,
          months: [],
          year: filters.year || item.year || 'N/A',
        };
      }

      aggregated[key].absentDays += Number(item.absentDays) || 0;
      aggregated[key].workingDays += Number(item.workingDays) || 0;
      aggregated[key].occasionsOfAbsence += Number(item.occasionsOfAbsence) || 0;

      if (item.month && !aggregated[key].months.includes(item.month)) {
        aggregated[key].months.push(item.month);
      }

      // Always recalculate Bradford Factor for aggregated data
      aggregated[key].bradfordFactor = Math.round(
          Math.pow(aggregated[key].occasionsOfAbsence, 2) * aggregated[key].absentDays
      );
    });

    return Object.values(aggregated);
  }, [filters.year]);

  const applySearchFilter = useCallback((data) => {
    if (!Array.isArray(data)) return data;
    if (!searchTerm) return data;

    return data.filter((item) =>
        item.userName?.toLowerCase().includes(searchTerm.toLowerCase())
    );
  }, [searchTerm]);

  const applyRiskFilter = useCallback((data) => {
    if (!Array.isArray(data)) return data;
    if (!filters.riskLevel) return data;

    return data.filter(item => {
      const bf = Number(item.bradfordFactor) || 0;

      switch(filters.riskLevel) {
        case 'high':
          return bf > RISK_THRESHOLDS.high;
        case 'medium':
          return bf > RISK_THRESHOLDS.medium && bf <= RISK_THRESHOLDS.high;
        case 'low':
          return bf <= RISK_THRESHOLDS.medium;
        default:
          return true;
      }
    });
  }, [filters.riskLevel]);

  const applyAllFilters = useCallback((data) => {
    let result = data;

    // First apply view mode (yearly/monthly aggregation)
    if (filters.viewMode === 'yearly') {
      result = aggregateDataByEmployee(result);
    }

    // Then apply search filter
    result = applySearchFilter(result);

    // Finally apply risk filter
    result = applyRiskFilter(result);

    return result;
  }, [filters.viewMode, filters.riskLevel, searchTerm, aggregateDataByEmployee, applySearchFilter, applyRiskFilter]);

  const fetchAllData = async (params) => {
    try {
      let allData = [];
      let currentPage = 1;
      let totalPages = 1;

      // First request to get total pages
      const initialResponse = await axios.get(
          filters.viewMode === 'yearly' && filters.year
              ? `https://backend.ems.uk.hiradentallab.com/api/attendance/yearly-bradford-factors?${params.toString()}`
              : `https://backend.ems.uk.hiradentallab.com/api/attendance/bradford-factor?${params.toString()}`
      );

      const initialData = initialResponse.data.data || { docs: [], totalPages: 1 };
      totalPages = initialData.totalPages || 1;

      // If only one page, return the data
      if (totalPages === 1) {
        return initialData.docs || [];
      }

      // Fetch all pages in parallel
      const pageRequests = [];
      for (let page = 1; page <= totalPages; page++) {
        const pageParams = new URLSearchParams(params);
        pageParams.set('page', page);
        pageRequests.push(
            axios.get(
                filters.viewMode === 'yearly' && filters.year
                    ? `https://backend.ems.uk.hiradentallab.com/api/attendance/yearly-bradford-factors?${pageParams.toString()}`
                    : `https://backend.ems.uk.hiradentallab.com/api/attendance/bradford-factor?${pageParams.toString()}`
            )
        );
      }

      const responses = await Promise.all(pageRequests);
      responses.forEach(response => {
        const pageData = response.data.data?.docs || [];
        allData = allData.concat(pageData);
      });

      return allData;
    } catch (error) {
      console.error('Error fetching all data:', error);
      throw error;
    }
  };

  const fetchBradfordFactors = useCallback(async () => {
    try {
      setLoading(true);
      setError(null);

      const params = new URLSearchParams();
      if (filters.month) params.append('month', filters.month);
      if (filters.year) params.append('year', filters.year);
      if (searchTerm) params.append('search', searchTerm);
      params.append('limit', 100); // Increase limit to reduce requests

      // Fetch ALL data first (all pages)
      const allData = await fetchAllData(params);

      if (!allData || allData.length === 0) {
        setRawData([]);
        setFilteredData([]);
        setDisplayData([]);
        setDataAvailable(false);
        setPagination({
          totalDocs: 0,
          totalPages: 1,
          hasNextPage: false,
          hasPrevPage: false,
        });
        setOverallRiskCounts({ high: 0, medium: 0, low: 0 });
        return;
      }

      setRawData(allData);
      setDataAvailable(true);

      // Apply all filters to the complete dataset
      const filtered = applyAllFilters(allData);
      setFilteredData(filtered);

      // Calculate risk counts from the filtered data
      const counts = calculateRiskCounts(filtered);
      setOverallRiskCounts(counts);

      // Apply pagination to the filtered data
      const startIndex = (filters.page - 1) * filters.limit;
      const paginatedData = filtered.slice(startIndex, startIndex + filters.limit);
      setDisplayData(paginatedData);

      // Update pagination info
      setPagination({
        totalDocs: filtered.length,
        totalPages: Math.ceil(filtered.length / filters.limit),
        hasNextPage: startIndex + filters.limit < filtered.length,
        hasPrevPage: filters.page > 1,
      });

    } catch (error) {
      console.error('Error fetching Bradford Factors:', error);
      if (error.response?.status === 404) {
        setDataAvailable(false);
        setError(null);
      } else if (error.response) {
        setError(`Server error: ${error.response.data?.message || 'Unknown error'}`);
      } else {
        setError('Client-side error. Please check your code or network connection.');
      }
      setRawData([]);
      setFilteredData([]);
      setDisplayData([]);
      setOverallRiskCounts({ high: 0, medium: 0, low: 0 });
      setPagination({
        totalDocs: 0,
        totalPages: 1,
        hasNextPage: false,
        hasPrevPage: false,
      });
    } finally {
      setLoading(false);
    }
  }, [filters, searchTerm, applyAllFilters]);

  useEffect(() => {
    fetchBradfordFactors();
  }, [fetchBradfordFactors]);

  const handlePageChange = (pageNumber) => {
    if (pageNumber >= 1 && pageNumber <= pagination.totalPages) {
      setFilters((prev) => ({ ...prev, page: pageNumber }));

      // Update display data for the new page
      const startIndex = (pageNumber - 1) * filters.limit;
      const paginatedData = filteredData.slice(startIndex, startIndex + filters.limit);
      setDisplayData(paginatedData);
    }
  };
  const handleSearch = (e) => {
    e.preventDefault();
    setFilters((prev) => ({ ...prev, page: 1 }));
    fetchBradfordFactors();
  };

  const handleFilterChange = (e) => {
    const { name, value } = e.target;
    setFilters((prev) => ({ ...prev, [name]: value, page: 1 }));
  };

  const changeViewMode = (mode) => {
    setFilters((prev) => ({
      ...prev,
      viewMode: mode,
      month: mode === 'yearly' ? '' : prev.month,
      year: mode === 'yearly' ? currentYear.toString() : prev.year,
      page: 1,
      riskLevel: null,
    }));
    setSuccess(`Now showing ${mode} aggregated data`);
    setTimeout(() => setSuccess(null), 3000);
  };

  const resetFilters = () => {
    setFilters({
      month: '',
      year: currentYear.toString(),
      page: 1,
      limit: 10,
      viewMode: 'yearly',
      riskLevel: null,
    });
    setSearchTerm('');
    setSuccess('Filters reset successfully');
    setTimeout(() => setSuccess(null), 3000);
  };

  const applyCurrentMonth = () => {
    setFilters((prev) => ({
      ...prev,
      month: currentMonth.toString(),
      year: currentYear.toString(),
      page: 1,
      viewMode: 'monthly',
      riskLevel: null,
    }));
    setSuccess(`Current month (${moment().format('MMMM YYYY')}) applied`);
    setTimeout(() => setSuccess(null), 3000);
  };

  const handleRiskLevelClick = (level) => {
    setFilters((prev) => ({
      ...prev,
      riskLevel: prev.riskLevel === level ? null : level,
      page: 1,
    }));
    setSuccess(`Showing ${level} risk employees`);
    setTimeout(() => setSuccess(null), 3000);
    fetchBradfordFactors(); // Force re-fetch on risk level change
  };

  const handleTotalRecordsClick = () => {
    setFilters((prev) => ({
      ...prev,
      riskLevel: null,
      page: 1,
    }));
    setSuccess('Showing all records');
    setTimeout(() => setSuccess(null), 3000);
    fetchBradfordFactors(); // Force re-fetch on total records click
  };

  const getPageNumbers = () => {
    const pages = [];
    const totalPages = pagination.totalPages;
    const currentPage = filters.page;

    pages.push(1);
    if (currentPage > 3) pages.push('...');
    if (currentPage > 2) pages.push(currentPage - 1);
    if (currentPage !== 1 && currentPage !== totalPages) pages.push(currentPage);
    if (currentPage < totalPages - 1) pages.push(currentPage + 1);
    if (currentPage < totalPages - 2) pages.push('...');
    if (totalPages > 1) pages.push(totalPages);

    return pages;
  };

  const handleGenerateReport = async () => {
    try {
      setGenerating(true);
      setError(null);

      const monthToUse = generateMonth || currentMonth;
      const yearToUse = generateYear || currentYear;

      if (!monthToUse || !yearToUse) {
        setError('Month and year are required');
        return;
      }

      const response = await axios.post('https://backend.ems.hiradentallab.com/api/attendance/bradford-factor/calculate', {
        month: monthToUse,
        year: yearToUse,
      });

      if (response.data.success) {
        setSuccess(
            `Bradford Factors for ${moment(`${yearToUse}-${monthToUse}`, 'YYYY-M').format('MMMM YYYY')} generated successfully!`
        );
        fetchBradfordFactors();
        setShowGenerateModal(false);
        setGenerateMonth('');
        setGenerateYear('');
        setTimeout(() => setSuccess(null), 5000);
      } else {
        setError(response.data.message || 'Failed to generate report');
      }
    } catch (error) {
      console.error('Error generating report:', error);
      setError(error.response?.data?.message || 'Failed to generate report. Please try again.');
    } finally {
      setGenerating(false);
    }
  };

  const getFilterStatus = () => {
    if (!dataAvailable) {
      return `No data available for the selected filters${searchTerm ? ` and search term "${searchTerm}"` : ''}`;
    }

    let status = '';
    if (filters.viewMode === 'yearly' && filters.year) {
      status = `Showing yearly aggregated data for ${filters.year}`;
    } else if (filters.viewMode === 'yearly') {
      status = 'Showing yearly aggregated data for all years';
    } else if (filters.month && filters.year) {
      status = `Showing monthly data for ${moment(`${filters.year}-${filters.month}`, 'YYYY-M').format('MMMM YYYY')}`;
    } else if (filters.month) {
      status = `Showing data for ${moment().month(filters.month - 1).format('MMMM')} (all years)`;
    } else if (filters.year) {
      status = `Showing data for year ${filters.year}`;
    } else {
      status = 'Showing all data';
    }

    if (filters.riskLevel) {
      status += `, filtered by ${filters.riskLevel} risk`;
    }
    if (searchTerm) {
      status += `, searching for "${searchTerm}"`;
    }

    return status;
  };

  return (
      <div className="bradfactor-container">
        <div className="bradfactor-header">
          <h1 className="bradfactor-title">
            <FaUsers className="settings-icon" /> Employees Bradford Factor
          </h1>
          <p className="bradfactor-subtitle">Monitor and analyze employee absence patterns</p>
        </div>

        {error && (
            <Alert variant="danger" className="animated-alert" onClose={() => setError(null)} dismissible>
              {error}
            </Alert>
        )}
        {success && (
            <Alert variant="success" className="animated-alert" onClose={() => setSuccess(null)} dismissible>
              {success}
            </Alert>
        )}

        <Row className="mb-4">
          <Col md={3}>
            <Card
                className={`summary-card total-employees ${!filters.riskLevel ? 'active-risk-filter' : ''}`}
                onClick={handleTotalRecordsClick}
                role="button"
            >
              <Card.Body>
                <Card.Title>Total Records</Card.Title>
                <Card.Text>{loading ? <Spinner size="sm" /> : pagination.totalDocs.toLocaleString()}</Card.Text>
                {!filters.riskLevel && (
                    <div className="active-filter-indicator">Showing All</div>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card
                className={`summary-card high-risk ${filters.riskLevel === 'high' ? 'active-risk-filter' : ''}`}
                onClick={() => handleRiskLevelClick('high')}
                role="button"
            >
              <Card.Body>
                <Card.Title>High Risk</Card.Title>
                <Card.Text>
                  {loading ? <Spinner size="sm" /> : overallRiskCounts.high.toLocaleString()}
                </Card.Text>
                {filters.riskLevel === 'high' && (
                    <div className="active-filter-indicator">Active Filter</div>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card
                className={`summary-card medium-risk ${filters.riskLevel === 'medium' ? 'active-risk-filter' : ''}`}
                onClick={() => handleRiskLevelClick('medium')}
                role="button"
            >
              <Card.Body>
                <Card.Title>Medium Risk</Card.Title>
                <Card.Text>
                  {loading ? <Spinner size="sm" /> : overallRiskCounts.medium.toLocaleString()}
                </Card.Text>
                {filters.riskLevel === 'medium' && (
                    <div className="active-filter-indicator">Active Filter</div>
                )}
              </Card.Body>
            </Card>
          </Col>
          <Col md={3}>
            <Card
                className={`summary-card low-risk ${filters.riskLevel === 'low' ? 'active-risk-filter' : ''}`}
                onClick={() => handleRiskLevelClick('low')}
                role="button"
            >
              <Card.Body>
                <Card.Title>Low Risk</Card.Title>
                <Card.Text>
                  {loading ? <Spinner size="sm" /> : overallRiskCounts.low.toLocaleString()}
                </Card.Text>
                {filters.riskLevel === 'low' && (
                    <div className="active-filter-indicator">Active Filter</div>
                )}
              </Card.Body>
            </Card>
          </Col>
        </Row>

        <Card className="bradfactor-filters mb-4">
          <Card.Body>
            <Card.Title className="filter-title">
              <FaFilter className="me-2" /> Filters
            </Card.Title>

            <Tabs activeKey={filters.viewMode} onSelect={(k) => changeViewMode(k)} className="mb-4" id="view-mode-tabs">
              <Tab eventKey="yearly" title="Yearly View" />
              <Tab eventKey="monthly" title="Monthly View" />
            </Tabs>

            <Row className="mb-3">
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Month {filters.viewMode === 'yearly' && '(disabled in yearly view)'}</Form.Label>
                  <Form.Control
                      as="select"
                      name="month"
                      value={filters.month}
                      onChange={handleFilterChange}
                      className="mb-2"
                      disabled={filters.viewMode === 'yearly'}
                  >
                    <option value="">All Months</option>
                    {Array.from({ length: 12 }, (_, i) => (
                        <option key={i + 1} value={i + 1}>
                          {moment().month(i).format('MMMM')}
                        </option>
                    ))}
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col md={3}>
                <Form.Group>
                  <Form.Label>Year</Form.Label>
                  <Form.Control
                      as="select"
                      name="year"
                      value={filters.year}
                      onChange={handleFilterChange}
                      className="mb-2"
                  >
                    <option value="">All Years</option>
                    {Array.from({ length: 5 }, (_, i) => {
                      const year = currentYear - i;
                      return <option key={year} value={year}>{year}</option>;
                    })}
                  </Form.Control>
                </Form.Group>
              </Col>
              <Col md={3} className="d-flex align-items-end">
                <div className="d-flex flex-wrap gap-2">
                  <Button variant="outline-primary" onClick={applyCurrentMonth} disabled={filters.viewMode === 'yearly'}>
                    <FaSync className="me-2" /> Current Month
                  </Button>
                  <Button variant="outline-secondary" onClick={resetFilters}>
                    Reset All
                  </Button>
                </div>
              </Col>
              <Col md={3}>
                <Form onSubmit={handleSearch}>
                  <Form.Group controlId="employeeSearch" className="position-relative mb-0">
                    <Form.Label className="fw-medium small mb-1 visually-hidden">Search</Form.Label>
                    <div className="fancy-search-wrapper">
                      <Form.Control
                          type="search"
                          placeholder="Search employees..."
                          value={searchTerm}
                          onChange={(e) => setSearchTerm(e.target.value)}
                          className="fancy-search-input"
                      />
                      <FaSearch className="search-icon" />
                      <div className="search-highlight"></div>
                    </div>
                  </Form.Group>
                </Form>
              </Col>
            </Row>
            <Row className="align-items-center">
              <Col md={6}>
                <small className={`filter-status ${!dataAvailable ? 'text-warning' : 'text-muted'}`}>
                  {getFilterStatus()}
                </small>
              </Col>
              <Col md={6} className="d-flex justify-content-end">
                <Button
                    variant="primary"
                    className="generate-report-btn"
                    onClick={() => {
                      setShowGenerateModal(true);
                      setError(null);
                    }}
                >
                  <FaFileExport className="me-2" /> Generate Report
                </Button>
              </Col>
            </Row>
          </Card.Body>
        </Card>

        <Card className="bradfactor-table-container">
          <Card.Body>
            {loading ? (
                <div className="text-center my-5 py-5">
                  <Spinner animation="border" role="status" className="loading-spinner" variant="primary">
                    <span className="visually-hidden">Loading...</span>
                  </Spinner>
                  <p className="mt-3">Loading employee data...</p>
                </div>
            ) : !dataAvailable ? (
                <div className="text-center my-5 py-5">
                  <img
                      src="https://cdn-icons-png.flaticon.com/512/4076/4076478.png"
                      alt="No data"
                      className="mb-3"
                      style={{ width: '80px', opacity: 0.7 }}
                  />
                  <h5>No data available</h5>
                  <p className="text-muted">There are no records for the selected filters</p>
                  <Button variant="outline-primary" onClick={resetFilters}>
                    Reset Filters
                  </Button>
                </div>
            ) : (
                <>
                  <div className="table-responsive">
                    <Table striped hover className="bradfactor-table">
                      <thead>
                      <tr>
                        <th>#</th>
                        <th>Employee Name</th>
                        <th>Department</th>
                        {filters.viewMode === 'yearly' ? <th>Year</th> : <th>Month</th>}
                        <th className="text-center">Absent Days</th>
                        <th className="text-center">Working Days</th>
                        <th className="text-center">Occasions</th>
                        <th className="text-center">Bradford Factor</th>
                      </tr>
                      </thead>
                      <tbody>
                      {displayData.length > 0 ? (
                          displayData.map((item, index) => (
                              <tr key={item._id || `${item.userName}-${item.year || item.month}`}>
                                <td>{(filters.page - 1) * filters.limit + index + 1}</td>
                                <td className="fw-semibold">{item.userName || 'Unknown User'}</td>
                                <td>{item.department || 'Not specified'}</td>
                                <td>
                                  {filters.viewMode === 'yearly'
                                      ? item.year || 'N/A'
                                      : item.month
                                          ? moment(item.month, 'YYYY-MM').format('MMMM YYYY')
                                          : 'N/A'}
                                </td>
                                <td className="text-center">{item.absentDays || 0}</td>
                                <td className="text-center">{item.workingDays || 0}</td>
                                <td className="text-center">{item.occasionsOfAbsence || 0}</td>
                                <td className="text-center">
                            <span
                                className={`badge rounded-pill ${
                                    item.bradfordFactor > 100
                                        ? 'bg-danger'
                                        : item.bradfordFactor > 50
                                            ? 'bg-warning'
                                            : 'bg-success'
                                }`}
                            >
                              {item.bradfordFactor || 0}
                            </span>
                                </td>
                              </tr>
                          ))
                      ) : (
                          <tr>
                            <td colSpan="8" className="text-center py-4">
                              <div className="no-data-message">
                                <img
                                    src="https://cdn-icons-png.flaticon.com/512/4076/4076478.png"
                                    alt="No data"
                                    className="mb-3"
                                    style={{ width: '80px', opacity: 0.7 }}
                                />
                                <h5>{filters.riskLevel ? `No ${filters.riskLevel}-risk records found` : 'No matching records found'}</h5>
                                <p className="text-muted">Try adjusting your filters or search criteria</p>
                              </div>
                            </td>
                          </tr>
                      )}
                      </tbody>
                    </Table>
                  </div>

                  {displayData.length > 0 && (
                      <div className="d-flex justify-content-between align-items-center mt-3">
                        <div className="text-muted">
                          Showing {(filters.page - 1) * filters.limit + 1} to{' '}
                          {Math.min(filters.page * filters.limit, pagination.totalDocs)} of {pagination.totalDocs} entries
                        </div>
                        <Pagination>
                          <Pagination.Prev
                              onClick={() => handlePageChange(filters.page - 1)}
                              disabled={!pagination.hasPrevPage}
                          />
                          {getPageNumbers().map((page, index) =>
                              page === '...' ? (
                                  <Pagination.Ellipsis key={`ellipsis-${index}`} disabled />
                              ) : (
                                  <Pagination.Item
                                      key={page}
                                      active={page === filters.page}
                                      onClick={() => handlePageChange(page)}
                                  >
                                    {page}
                                  </Pagination.Item>
                              )
                          )}
                          <Pagination.Next
                              onClick={() => handlePageChange(filters.page + 1)}
                              disabled={!pagination.hasNextPage}
                          />
                        </Pagination>
                      </div>
                  )}
                </>
            )}
          </Card.Body>
        </Card>

        <Modal show={showGenerateModal} onHide={() => setShowGenerateModal(false)} centered>
          <Modal.Header closeButton className="bg-primary text-white">
            <Modal.Title>
              <FaFileExport className="me-2" /> Generate Monthly Report
            </Modal.Title>
          </Modal.Header>
          <Modal.Body>
            <Form>
              <Form.Group className="mb-3">
                <Form.Label>Month</Form.Label>
                <Form.Control
                    as="select"
                    value={generateMonth}
                    onChange={(e) => setGenerateMonth(e.target.value)}
                    className="form-select-lg"
                >
                  <option value="">Select Month</option>
                  {Array.from({ length: 12 }, (_, i) => (
                      <option key={i + 1} value={i + 1}>
                        {moment().month(i).format('MMMM')}
                      </option>
                  ))}
                </Form.Control>
              </Form.Group>
              <Form.Group className="mb-3">
                <Form.Label>Year</Form.Label>
                <Form.Control
                    as="select"
                    value={generateYear}
                    onChange={(e) => setGenerateYear(e.target.value)}
                    className="form-select-lg"
                >
                  <option value="">Select Year</option>
                  {Array.from({ length: 5 }, (_, i) => {
                    const year = currentYear - i;
                    return <option key={year} value={year}>{year}</option>;
                  })}
                </Form.Control>
              </Form.Group>
            </Form>
            <Alert variant="info" className="mt-3">
              <strong>Note:</strong> This will calculate Bradford Factors for all employees for the selected month.
              Reports can only be generated once per month.
            </Alert>
          </Modal.Body>
          <Modal.Footer>
            <Button
                variant="outline-secondary"
                onClick={() => {
                  setShowGenerateModal(false);
                  setError(null);
                }}
            >
              Cancel
            </Button>
            <Button
                variant="primary"
                onClick={handleGenerateReport}
                disabled={generating || !generateMonth || !generateYear}
                className="d-flex align-items-center"
            >
              {generating ? (
                  <>
                    <Spinner as="span" animation="border" size="sm" role="status" aria-hidden="true" className="me-2" />
                    Generating...
                  </>
              ) : (
                  <>
                    <FaFileExport className="me-2" />
                    Generate Report
                  </>
              )}
            </Button>
          </Modal.Footer>
        </Modal>
      </div>
  );
}